/** Application shell: screen transitions, gameplay orchestration, display setup,
 * diagnostics, and the renderer HMR boundary. See docs/ARCHITECTURE.md.
 * Simulation uses 320×180 logical pixels; both canvases have 1280×720 backing
 * stores. HTML editor controls share the same fitted frame.
 */

import { mountPlaytest } from '../tools/playtest/viewer'
import { Camera } from './core/camera'
import { liveReference } from './core/live-reference'
import { loadScalingMode, saveScalingMode } from './core/scaling'
import { TouchControls } from './core/touch'
import { GameLoop } from './core/loop'
import { Input } from './core/input'
import {
  DESIGN_H,
  DESIGN_W,
  DT,
  SCALING_MODES,
  TILE,
  VIEW_W,
  applyIdentityTransform,
  applyUiTransform,
  applyWorldTransform,
  fitDisplay,
  view,
} from './engine/constants'
import {
  isAudioMuted,
  setAudioMuted,
  resumeAudio,
  sfxStomp,
} from './core/audio'
import {
  clearHudPopups,
  drawHud,
  drawHudPopups,
  spawnHudPopup,
  tickHudPopups,
} from './engine/hud'
import { Renderer3D } from './engine3d/scene'
import { Level, emptyLevel } from './game/level'
import { Player, rectsOverlap } from './game/player'
import type { LevelData } from './game/types'
import { ENEMY_KINDS } from './game/entities' // registers entity factories
import { resolveInteractions } from './game/interactions'
import { SCORE } from './game/behaviours'
import { openEditor } from './editor/editor'
import type { SavedCourse } from './editor/types'
import { cloneLevelData } from './levels/storage'
import {
  EventLog,
  Recorder,
  Rng,
  classifyDeath,
  contactSource,
  type DeathContact,
} from './core/diagnostics'
import { Slope, type SolidQuery } from './engine/collision'
import {
  courseByName,
  listCourses,
  refreshCourses,
  type CourseEntry,
} from './levels/library'
import {
  chapterBounds,
  courseBlurb,
  courseDifficulty,
  scrollWindow,
  themeColor,
} from './levels/course-meta'
import { COURSE_LIST } from './levels/courses'
import { createDebug, setDebugCourses } from './core/debug'

type AppState =
  | 'title'
  | 'select'
  | 'play'
  | 'paused'
  | 'dead'
  | 'won'
  | 'gameover'
  | 'editor'

/**
 * How long the death animation holds before the respawn or the game-over
 * screen. `updateDead` integrates the hop itself because the simulation is
 * stopped while the body is dying.
 */
const DEATH_PAUSE = 1.1
/** Frames of upward hop at the start of the death animation. */
const DEATH_HOP_FRAMES = 8
/**
 * Spawn protection: frames of invulnerability granted when a course starts and
 * when a death respawns the player.
 *
 * Without it the first enemy on the opening flat can walk into a player who is
 * still reading the controls, and every respawn drops him back in front of the
 * same enemy. The blink the damage invulnerability already draws is the
 * feedback, and the enemy can still be stomped while it lasts.
 */
const SPAWN_GRACE_FRAMES = 150

/**
 * The two stacked canvases.
 *
 * `#world` is the WebGL surface: a 320x180 buffer holding the 3D scene, scaled
 * up by the compositor. `#screen` is the 2D interface layer at the full 1280x720
 * design resolution, transparent except where it draws.
 *
 * The split keeps pointer events and the editor's screen-space tile picking all still land on `#screen`,
 * because it is the top layer and its CSS box is identical to the WebGL one. An
 * side-on camera keeps the play plane aligned with the interface.
 */
const worldCanvas = document.getElementById('world') as HTMLCanvasElement
const canvas = document.getElementById('screen') as HTMLCanvasElement
const ctx = canvas.getContext('2d', {
  // Transparent, or the interface layer would be an opaque black sheet over the
  // 3D scene. Menus that want a solid background still fill it themselves.
  alpha: true,
}) as CanvasRenderingContext2D
const hintEl = document.getElementById('hint')

/** Font of the screen-space toast, duplicated so it can be measured. */
const NOTICE_FONT = 'bold 24px ui-monospace, "SF Mono", Menlo, monospace'

/**
 * Clear the interface layer, ignoring any active transform.
 *
 * This has to *erase* rather than paint black. The interface canvas is
 * transparent and sits over the WebGL layer, so filling it would hide the 3D
 * scene entirely -- which is exactly what happened the first time, and looked
 * like a renderer that had silently stopped working. States that genuinely want
 * an opaque background (the title, the course select) call `clearScreen`.
 */
function clearDevice(): void {
  applyIdentityTransform(ctx)
  ctx.clearRect(0, 0, canvas.width, canvas.height)
}

/** Show or hide the HTML control hints (they collide with the editor's UI). */
function setHintVisible(visible: boolean): void {
  if (hintEl !== null) hintEl.style.opacity = visible ? '' : '0'
}

if (ctx === null) throw new Error('canvas 2d context unavailable')

const input = new Input()
// The listeners stay attached for the life of the page, so the detach function
// `attach` returns is deliberately discarded.
void input.attach(window)

const camera = new Camera()
/** Camera for the scene behind the menus, drifting on its own. */
const backdropCamera = new Camera()
const liveRenderer = liveReference(new Renderer3D(worldCanvas))
const renderer3d = liveRenderer.value
const player = new Player()

/**
 * Draws any enabled debug overlay. A no-op until the dev block below installs
 * the real implementation, so production builds pay one empty call per frame.
 */
let debugDraw: (level: Level, cam: Camera) => void = () => {}

/**
 * Puts the on-screen controls on the right layer for the current screen.
 * Installed once the controls exist; the no-op default keeps `render` free of
 * null checks, the same pattern `debugDraw` uses.
 */
let syncTouchControls: (state: AppState) => void = () => {}

/**
 * True while the touch layer is the control scheme in use. Menu and HUD copy
 * reads it so a phone is told to tap where a desktop is told to press Space.
 */
let touchActive = false

class App {
  state: AppState = 'title'
  level: Level | null = null
  private deathTimer = 0
  private winTimer = 0
  /** Index into `listCourses()` for the selector. */
  private selected = 0
  /**
   * Name of the highlighted course. Tracked alongside the index because saving
   * a course can change the list length — a copy is appended — and an index
   * alone would slide onto a different course underneath the highlight.
   */
  private selectedCourse: string | null = null
  /** Hold-to-repeat for the select list (long catalogues). */
  private selectRepeatDir: -1 | 0 | 1 = 0
  private selectRepeatAt = 0
  totalCoins = 0
  /** Where to return to when the editor closes. */
  private editorReturn: AppState = 'play'
  private closeEditor: ReturnType<typeof openEditor> | null = null
  private viewer: ReturnType<typeof mountPlaytest> | null = null
  private previewReturn: AppState | null = null

  private beginPreview(kind: 'playtest' | 'play' = 'playtest'): void {
    history.pushState({ taviPreview: true }, '', '#' + kind)
    this.previewReturn = this.state === 'play' ? 'paused' : this.state
    document.getElementById('back-editor')!.hidden = this.state !== 'editor'
    document.getElementById('watch-bot')!.setAttribute('disabled', '')
  }

  backFromPreview(): void {
    if (this.previewReturn !== null) history.back()
  }

  restorePreview(): void {
    if (this.previewReturn === null) return
    this.viewer?.dispose()
    this.viewer = null
    this.state = this.previewReturn
    this.previewReturn = null
    document.getElementById('back-editor')!.hidden = true
    document.getElementById('watch-bot')!.removeAttribute('disabled')
    if (this.state === 'editor') this.closeEditor?.resume()
    canvas.focus()
  }
  /** Screen-space toast, drawn over whatever state we are in. */
  private notice: { text: string; until: number } | null = null

  // ── diagnostics ──────────────────────────────────────────────────────────
  //
  // The event log is the answer to "what happened", and it is owned here
  // because the shell is the only place that sees every route into a death.
  // See `classifyDeath` for why the cause cannot come from `Level.kill()`.
  readonly log = new EventLog()
  readonly recorder = new Recorder()
  /** Seeded so a bug report can name a seed and have it mean something. */
  readonly rng = Rng.fromString('tavi')
  /** Lethal contact seen on the frame the player died, for the classifier. */
  private deathContact: DeathContact = null
  /** Guards against logging the same death once per frame. */
  private lastLoggedDeathTick = -1
  /** Whether `noClip` is on. Read through `collisionWorld` every step. */
  private noclip = false
  /** Whether `godMode` is on. Read wherever lethality is decided. */
  private god = false
  /** Set by `__debug.kill()`, consumed by the next death classification. */
  private pendingDebugKill = false

  /**
   * The collision world the player is stepped against.
   *
   * `noClip` is implemented here rather than as a branch inside the collision
   * code, so the engine stays completely unaware of debugging. The wrapper is
   * only ever consulted when the flag is on, which is why the common path is
   * the level itself and costs nothing.
   */
  private collisionWorld(lv: Level): SolidQuery {
    if (!this.noclip) return lv
    if (this.noclipWorld === null) {
      this.noclipWorld = {
        isSolid: () => false,
        isOneWay: () => false,
        slopeAt: () => Slope.NONE,
      }
    }
    return this.noclipWorld
  }

  private noclipWorld: SolidQuery | null = null

  /** Turn the debug-only collision powers on or off, from one place. */
  setDebugFlags(flags: { noClip?: boolean; godMode?: boolean }): void {
    if (flags.noClip !== undefined) this.noclip = flags.noClip
    if (flags.godMode !== undefined) this.god = flags.godMode
  }

  get debugFlags(): { noClip: boolean; godMode: boolean } {
    return { noClip: this.noclip, godMode: this.god }
  }

  /**
   * Flag the next death as console-initiated.
   *
   * `__debug.kill()` calls the same `player.kill()` the game does, so without
   * this the log records `unknown` — which reads as "a death path failed to
   * claim this", i.e. as a bug in the game, every time someone tests the death
   * flow on purpose.
   */
  noteDebugKill(): void {
    this.pendingDebugKill = true
  }

  /**
   * Scene cost for `__debug.perf()`.
   *
   * Simulation fps and draw cost are different questions, and the 3D migration
   * made that separation real: a frame can be trivial to simulate and expensive
   * to rasterise, and only the second shows up as a dropped frame. Reporting
   * both is what tells you which side to look at.
   */
  sceneStats(): { calls: number; triangles: number } | null {
    return { calls: renderer3d.drawCalls, triangles: renderer3d.triangles }
  }

  /**
   * True while the editor owns input.
   *
   * Derived from the state rather than kept as its own flag. It used to be a
   * separate boolean, which is how `app.state` stayed `'play'` for the whole
   * time the editor was open: `__debug.snapshot()` reported a healthy, running
   * game that was in fact frozen, and that cost a debugging session. There is
   * now one source of truth and the snapshot cannot disagree with it.
   */
  get inEditor(): boolean {
    return this.state === 'editor'
  }

  constructor() {
    canvas.addEventListener('click', () => {
      void canvas.focus()
    })
    this.selectCourse(0)
    this.updateHint()
  }

  private updateHint(): void {
    if (hintEl === null) return
    hintEl.innerHTML =
      '<kbd>&larr;</kbd><kbd>&rarr;</kbd> move &middot; <kbd>Space</kbd> jump &middot; ' +
      '<kbd>Shift</kbd> run / fire &middot; <kbd>&darr;</kbd> duck / drop through &middot; ' +
      '<kbd>E</kbd> edit this course &middot; <kbd>R</kbd> restart &middot; <kbd>Esc</kbd> pause'
  }

  // ── state transitions ────────────────────────────────────────────────

  /**
   * Move the selector to `index`, remembering the course by name.
   *
   * Clamping rather than wrapping: the list can now be any length (a course
   * saved while you were in the menu lands at the bottom), and a bounded list
   * that scrolls is easier to read than one that jumps.
   */
  private selectCourse(index: number): void {
    const list = listCourses()
    if (list.length === 0) {
      this.selected = 0
      this.selectedCourse = null
      return
    }
    this.selected = Math.max(0, Math.min(list.length - 1, index))
    this.selectedCourse = list[this.selected]?.name ?? null
  }

  /**
   * Re-anchor the highlight after the library changes.
   *
   * Called whenever the course list may have been rewritten, i.e. after the
   * editor closes and whenever we enter the select screen.
   */
  private reanchorSelection(): void {
    if (this.selectedCourse === null) {
      this.selectCourse(0)
      return
    }
    const at = listCourses().findIndex((c) => c.name === this.selectedCourse)
    if (at >= 0) this.selected = at
    else this.selectCourse(this.selected)
  }

  /**
   * The HUD's `WORLD` label for a course, as `world-level`.
   *
   * Two worlds: built-in courses are world 1, and everything the player has
   * saved is world 2. Numbering each group from one gives every course a
   * stable, meaningful slot — save a course and it lands in 2-1, and the label
   * does not shuffle when an unrelated course is deleted, because the count is
   * positional within its own group rather than a global index.
   *
   * A course that is somehow not in the library (hand-edited storage, a level
   * handed straight to the runtime) still has to render *something*, so it falls
   * back to the shipped first slot rather than showing a blank or `NaN`.
   */
  worldLabel(name: string | null): string {
    if (name === null) return '1-1'
    const list = this.courses()
    const at = list.findIndex((c) => c.name === name)
    if (at < 0) return '1-1'
    const entry = list[at]
    if (entry === undefined) return '1-1'
    if (entry.builtIn) {
      // The shipped courses are numbered in library order, so a course keeps
      // its number for the life of the install.
      let n = 0
      for (let i = 0; i <= at; i++) {
        if (list[i]?.builtIn === true) n++
      }
      return `1-${n}`
    }
    let n = 0
    for (let i = 0; i <= at; i++) {
      if (list[i]?.builtIn === false) n++
    }
    return `2-${n}`
  }

  /** Everything playable right now: built-ins, then saved courses. */
  private courses(): CourseEntry[] {
    return listCourses()
  }

  startCourse(index: number): void {
    // The editor owns the screen while it is open, and `state` is what says so.
    // Nothing in the shell can start a course behind its back — but
    // `__debug.loadCourse` can, and a level loading under a live editor left
    // the two fighting over the frame.
    if (this.state === 'editor') this.closeEditorNow()
    const list = this.courses()
    const data = list[Math.max(0, Math.min(list.length - 1, index))]?.data
    if (data === undefined) {
      console.error('no such course', index)
      this.toSelect()
      return
    }
    this.selectCourse(index)
    this.loadLevel(data)
  }

  loadLevel(data: LevelData): void {
    // Clone on the way in. `Level` owns the object layer it is handed: a brick
    // head-bump calls `removeObject`, which splices `data.objects` in place, so
    // passing the shared `COURSES` entry meant one play session could delete
    // blocks from a shipped course for the rest of the session — and, because
    // the selector and the editor both read those same objects, for good.
    // `docs/LEVELS.md` and the reachability pass are measured against that
    // data, so it has to stay exactly as shipped.
    this.level = new Level(cloneLevelData(data), {
      onWin: () => {
        this.state = 'won'
        this.winTimer = 0
      },
      onDeath: () => {
        this.state = 'dead'
        this.deathTimer = 0
      },
    })
    this.totalCoins = 0
    this.lastLoggedDeathTick = -1
    this.deathContact = null
    this.pendingDebugKill = false
    this.level.player = player
    player.bind(this.level)
    player.placeAt(data.startX * TILE, data.startY * TILE)
    // A course always opens with the same grace a respawn gets, so the first
    // enemy on the opening flat cannot kill a player who is still reading the
    // on-screen controls.
    player.invuln = SPAWN_GRACE_FRAMES
    camera.setBounds(data.width, data.height)
    camera.snapToTile(data.startX, data.startY, 0, 0)
    clearHudPopups()
    this.state = 'play'
  }

  retry(): void {
    const lv = this.level
    if (lv === null) this.startCourse(this.selected)
    else this.loadLevel(courseByName(lv.data.name)?.data ?? lv.data)
  }

  toSelect(): void {
    clearHudPopups()
    this.state = 'select'
    // The library may have gained a course since we last looked, and the
    // highlight should still be on whatever the player was just playing.
    refreshCourses()
    this.reanchorSelection()
    this.syncDebugCourses()
  }

  toTitle(): void {
    this.state = 'title'
  }

  watchBot(): void {
    if (this.previewReturn !== null) return
    this.beginPreview()
    this.viewer = mountPlaytest(renderer3d, worldCanvas, canvas, () =>
      this.backFromPreview(),
    )
    this.state = 'paused'
  }

  // ── editor ──────────────────────────────────────────────────────────

  /**
   * Open the editor on `entry`, or on a blank course when it is null.
   *
   * Every path into the editor goes through here, so "which course is this
   * session editing" is decided in exactly one place. `source` is what makes a
   * built-in safe: it tells the editor the level it was handed is a shipped
   * original, so the save becomes a copy under a new name instead of an
   * overwrite (see `resolveSaveName`).
   */
  openEditorFor(entry: CourseEntry | null): void {
    if (this.state === 'editor') return
    // Where `E` came back to. The editor never changes `state` itself, so this
    // is a plain read of the screen we are leaving.
    this.editorReturn = this.state
    this.closeEditor = openEditor(
      canvas,
      ctx,
      () => {
        this.endEditor()
      },
      {
        initialLevel: entry?.data,
        source:
          entry === null || entry === undefined
            ? null
            : { name: entry.name, builtIn: entry.builtIn },
        onSave: (save) => this.announceSave(save),
        worldRenderer: renderer3d,
        onWatch: (data) => {
          this.beginPreview()
          this.viewer = mountPlaytest(
            renderer3d,
            worldCanvas,
            canvas,
            () => this.backFromPreview(),
            data,
          )
          this.state = 'paused'
        },
        onPlay: (data, warning) => {
          this.beginPreview('play')
          this.loadLevel(data)
          if (warning !== undefined) this.notify(warning)
        },
      },
    )
    this.state = 'editor'
    // The editor draws its own status line along the bottom of the frame, so
    // the HTML control hints would sit straight on top of it.
    setHintVisible(false)
  }

  /**
   * The `E` key, in every state.
   *
   * What it opens depends on where you are: the course you are playing, or the
   * highlighted course on the select screen, or — with nothing loaded, as from
   * the title — a blank one. That is the whole point of a course builder: the thing
   * in front of you is the thing you edit.
   */
  toggleEditor(): void {
    if (this.previewReturn !== null) {
      this.backFromPreview()
      return
    }
    if (this.state === 'editor') {
      this.closeEditorNow()
      return
    }
    if (this.state === 'select') {
      this.openEditorFor(this.courses()[this.selected] ?? null)
      return
    }
    this.openEditorFor(this.playingEntry())
  }

  /**
   * The library entry for the course in play, or null when nothing is loaded.
   *
   * Looking the name up rather than remembering the entry keeps a session
   * honest: a course that is *not* in the library is still editable, and is
   * treated as the user's own, so a save overwrites it instead of forking.
   */
  private playingEntry(): CourseEntry | null {
    const lv = this.level
    if (lv === null) return null
    const found = courseByName(lv.data.name)
    if (found !== null) return found
    return { name: lv.data.name, data: lv.toData(), builtIn: false }
  }

  /** Tear the editor down if it is open, and put the shell back as it was. */
  private closeEditorNow(): void {
    const close = this.closeEditor
    this.closeEditor = null
    if (close !== null) close.close()
    // `close` runs `endEditor` synchronously. This is the belt to that braces:
    // if the editor failed to open in the first place, nothing called it.
    if (this.state === 'editor') this.endEditor()
  }

  private endEditor(): void {
    this.closeEditor = null
    this.state = this.editorReturn
    setHintVisible(true)
    // A course may have been written, renamed or re-filed on the way out, so
    // the selector's list is re-read rather than assumed.
    refreshCourses()
    this.reanchorSelection()
    this.syncDebugCourses()
  }

  /**
   * Say what the editor's save did.
   *
   * The editor's own toasts cannot do this job: it stops rendering the instant
   * it closes, which is exactly the moment it has something to report. The
   * rename case matters most — "I edited Green Hills, where is it" is answered
   * by `SAVED AS GREEN HILLS 2`.
   */
  private announceSave(save: SavedCourse): void {
    if (!save.durable) this.notify(`${save.name} - SESSION ONLY`)
    else if (save.renamed) this.notify(`SAVED AS ${save.name.toUpperCase()}`)
    else this.notify(`SAVED ${save.name.toUpperCase()}`)
  }

  private notify(text: string): void {
    this.notice = { text, until: performance.now() + 2600 }
  }

  /** Keep `__debug.courses()` honest about what can actually be played. */
  private syncDebugCourses(): void {
    if (!import.meta.env.DEV) return
    setDebugCourses(this.courses().map((c) => c.name))
  }

  // ── simulation ──────────────────────────────────────────────────────

  update(): void {
    if (this.viewer) {
      this.viewer.update()
      return
    }
    // `E` works in every state, and the press is consumed here: the same
    // keypress must not also be read as a menu confirm or a jump in the frame
    // it opens or closes the editor.
    if (input.justPressed('editor')) {
      this.toggleEditor()
      return
    }

    switch (this.state) {
      case 'title':
        this.updateTitle()
        return
      case 'select':
        this.updateSelect()
        return
      case 'editor':
        // The editor owns input and drives its own frame; the game does not
        // step behind it.
        return
      case 'won':
        this.updateWin()
        return
      case 'gameover':
        if (input.justPressed('confirm') || input.justPressed('jump'))
          this.retry()
        if (input.justPressed('cancel')) this.toSelect()
        return
      case 'paused':
        // Escape (or any confirm) resumes; the quit key is the only way out of
        // the course from here, so a pause never drops the run by accident.
        if (
          input.justPressed('cancel') ||
          input.justPressed('confirm') ||
          input.justPressed('jump')
        ) {
          this.state = 'play'
          return
        }
        if (input.justPressed('quit')) this.toSelect()
        return
      case 'dead':
        this.updateDead()
        return
      default:
        this.updatePlay()
    }
  }

  private updateTitle(): void {
    // `toSelect` rather than a bare assignment, so the course list is re-read
    // and the highlight is anchored before the menu is drawn.
    if (input.justPressed('confirm') || input.justPressed('jump'))
      this.toSelect()
  }

  private updateSelect(): void {
    if (input.justPressed('cancel')) {
      this.selectRepeatDir = 0
      this.toTitle()
      return
    }

    const list = this.courses()
    const { workshopEnd } = chapterBounds(list)
    if (input.justPressed('left')) {
      this.selectCourse(0)
    } else if (input.justPressed('right')) {
      if (workshopEnd < list.length) this.selectCourse(workshopEnd)
      else this.selectCourse(Math.max(0, list.length - 1))
    }

    const now = performance.now()
    const down = input.isDown('down') ? 1 : input.isDown('up') ? -1 : 0
    if (down === 0) {
      this.selectRepeatDir = 0
    } else if (input.justPressed('down') || input.justPressed('up')) {
      this.selectCourse(this.selected + down)
      this.selectRepeatDir = down as -1 | 1
      this.selectRepeatAt = now + 220
    } else if (down === this.selectRepeatDir && now >= this.selectRepeatAt) {
      this.selectCourse(this.selected + down)
      this.selectRepeatAt = now + 70
    }

    if (input.justPressed('confirm') || input.justPressed('jump')) {
      this.selectRepeatDir = 0
      this.startCourse(this.selected)
    }
  }

  private updateDead(): void {
    this.deathTimer += DT
    tickHudPopups()
    // Death hop: a small upward arc, then a fall that carries the body off the
    // bottom of the screen while the pause runs out.
    if (this.deathTimer < DEATH_HOP_FRAMES * DT) player.vy = -6
    else player.vy = Math.min(player.vy + 0.45, 8)
    player.y += player.vy
    // Keep framing the hop. Play is the only other state that advances the
    // camera, so without this the body leaves the frozen view.
    camera.update(player.x, player.y, player.w, player.h)
    // The pause is load-bearing: the guard used to test a count-up timer for
    // `<= 0`, so death resolved on the next frame. There was no visible death
    // at all, and a player killed at the spawn point was standing back on it
    // before the sprite had moved.
    if (this.deathTimer < DEATH_PAUSE) return
    if ((this.level?.stats.lives ?? 0) <= 0) {
      this.state = 'gameover'
    } else {
      const lv = this.level
      if (lv !== null) {
        lv.respawn()
        camera.snapToWorld(player.x, player.y, player.w, player.h)
        clearHudPopups()
        // Re-entry protection: the enemy that killed him is still nearby.
        player.invuln = SPAWN_GRACE_FRAMES
        this.state = 'play'
      }
    }
  }

  private updateWin(): void {
    this.winTimer += DT
    if (this.winTimer <= 1.2) return
    // Space / confirm: next course when there is one, otherwise the selector.
    if (input.justPressed('confirm') || input.justPressed('jump')) {
      this.advanceAfterWin()
      return
    }
    // Esc / Q: always leave for the course list.
    if (input.justPressed('cancel') || input.justPressed('quit')) {
      this.toSelect()
      return
    }
  }

  /**
   * After a clear: start the next library entry, or fall back to the selector
   * when this was the last course.
   */
  private advanceAfterWin(): void {
    const list = this.courses()
    const at = list.findIndex((entry) => entry.name === this.level?.data.name)
    const next = at + 1
    if (at >= 0 && next < list.length) {
      const stats = this.level?.stats
      this.startCourse(next)
      if (stats !== undefined && this.level !== null) {
        Object.assign(this.level.stats, {
          lives: stats.lives,
          coins: stats.coins,
          score: stats.score,
        })
        this.totalCoins = stats.coins
      }
      return
    }
    this.toSelect()
  }

  private updatePlay(): void {
    const lv = this.level
    if (lv === null) return

    if (input.justPressed('restart')) {
      // Restart is how you test an edit: "E, change something, close, R" has to
      // put you back into the level as it now is on disk. Re-read it from the
      // library, which is a no-op for a course that has not been edited.
      this.retry()
      return
    }
    if (input.justPressed('cancel')) {
      // Pause, not quit. The hint bar has always said "Esc pause", and a course
      // is the one place where a mis-read Escape should not throw away the run;
      // the paused screen owns the actual exit.
      this.state = 'paused'
      return
    }

    // Read input into the player.
    player.input.left = input.isDown('left')
    player.input.right = input.isDown('right')
    player.input.up = input.isDown('up')
    player.input.down = input.isDown('down')
    player.input.run = input.isDown('run')
    player.input.jump = input.isDown('jump')
    player.input.jumpPressed = input.justPressed('jump')

    lv.publishDynamicSolids()
    player.update(this.collisionWorld(lv), lv)
    lv.step()

    /**
     * Evidence for the death log, gathered before anything can be cleaned up.
     *
     * Every route into death funnels through one argument-less `Level.kill()`,
     * which is correct for the game but destroys the cause at the exact moment
     * it becomes interesting. The shell is the only place that can see all the
     * routes, so it records what it saw and hands it to `classifyDeath`.
     */
    const timedOut =
      lv.status === 'dead' && lv.data.time > 0 && lv.stats.time <= 0

    // One production interaction pass, shared with the playtest bot. The shell
    // keeps ownership of the diagnostics: it is still the only place that maps
    // the returned contact onto `deathContact` and the death classifier.
    const contact = resolveInteractions(lv, player, { god: this.god })
    this.deathContact ??= contact
    // Look-ahead: facing, run speed, and held up/down pull the viewport toward
    // where the player is headed so gaps and platforms ahead stay readable.
    camera.update(player.x, player.y, player.w, player.h, {
      facing: player.facing,
      vx: player.vx,
      lookUp: player.input.up,
      lookDown: player.input.down,
    })

    const belowWorld = player.y > lv.height * TILE + TILE * 2
    if (belowWorld) lv.kill()

    /**
     * The player sets its own `alive = false` when something lethal touches it
     * (an enemy, a spike, a lava tile). `Player.kill()` deliberately does not
     * know about the level's status machine, so without this bridge a lethal
     * touch would silently delete Tavi — the game would keep running with an
     * invisible player and the clock would tick on forever. `Level.kill()` is
     * idempotent, so routing both death causes through it is safe.
     */
    if (!player.alive) lv.kill()

    // One log line per death, with the cause resolved rather than guessed at.
    if (lv.status === 'dead' && this.lastLoggedDeathTick !== lv.tick) {
      this.lastLoggedDeathTick = lv.tick
      const cause = this.pendingDebugKill
        ? 'debug'
        : classifyDeath({
            belowWorld,
            timedOut,
            contact: this.deathContact,
            playerDead: !player.alive,
          })
      this.pendingDebugKill = false
      this.log.death(lv.tick, cause, {
        source: contactSource(this.deathContact),
        tile: [Math.floor(player.x / TILE), Math.floor(player.y / TILE)],
        data: { course: lv.data.name },
      })
      this.recorder.noteDeath()
    }
    this.deathContact = null

    // Player/enemy interactions.
    /**
     * The Shield Prism pass.
     *
     * Restricted to `ENEMY_KINDS`, which is the point: this used to delete
     * *anything* overlapping the player, so brushing a coin, a poof or your own
     * fireball while shielded paid 200 points a time and silently destroyed
     * pickups. `resolveInteractions` has already collected the real pickups
     * this frame, but the prism pass runs over the whole entity list, so `poof`,
     * `coin-pop` and `fireball` were all fair game. The prism should be a weapon,
     * not a scoring exploit.
     */
    if (lv.status === 'playing' && player.alive && player.isShielded) {
      for (const e of lv.entities) {
        if (!e.alive || !ENEMY_KINDS.has(e.kind)) continue
        if (!rectsOverlap(player.rect, e)) continue
        e.alive = false
        lv.addScore(SCORE.stomp)
        sfxStomp()
        spawnHudPopup(e.x + e.w / 2, e.y, `+${SCORE.stomp}`, '#fcfcfc')
      }
    }

    tickHudPopups()

    this.totalCoins = lv.stats.coins

    // Capture after everything that can move him has moved him, so a recording
    // is a record of the frame that actually happened rather than of the
    // player's half of it.
    if (this.recorder.isRecording) {
      this.recorder.capture({
        tick: lv.tick,
        x: player.x,
        y: player.y,
        vx: player.vx,
        vy: player.vy,
        onGround: player.onGround,
        power: player.power,
        input: { ...player.input },
      })
    }
  }

  // ── rendering ───────────────────────────────────────────────────────

  render(): void {
    if (this.viewer) {
      syncTouchControls('editor')
      setHintVisible(false)
      this.viewer.render()
      return
    }
    // The touch layer is told the screen before anything draws, because the
    // menu copy below adapts to whether it is the control scheme in use.
    syncTouchControls(this.state)
    setHintVisible(this.state === 'play' && !touchActive)
    clearDevice()
    this.drawScene()
    this.drawNotice()
  }

  /**
   * Everything the current state draws.
   *
   * The editor is a case here rather than a guard above the switch, so the
   * state machine is the single answer to "what is on screen" — the same answer
   * `update()` acts on and `__debug.snapshot()` reports.
   */
  private drawScene(): void {
    // The 3D scene is the bottom layer in every state, including the menus, so
    // a parked camera shows the course behind the title rather than a void.
    // Menus dim it rather than covering it.
    if (this.state !== 'editor') {
      renderer3d.setViewport(null)
      this.drawMenuBackdrop()
    }
    switch (this.state) {
      case 'title':
        applyUiTransform(ctx)
        this.drawTitle()
        return
      case 'select':
        applyUiTransform(ctx)
        this.drawSelect()
        return
      case 'gameover':
        applyUiTransform(ctx)
        this.drawGameOver()
        return
      case 'editor':
        // The editor draws in world coordinates and expects the world
        // transform. It runs its own RAF registered after ours, so its chrome
        // lands on top of this every frame; all that is left here is the
        // debug overlay, which is worth keeping visible underneath.
        applyWorldTransform(ctx)
        if (this.level !== null) debugDraw(this.level, camera)
        return
      default:
        this.drawPlay()
    }
  }

  /**
   * A level to show behind the menus.
   *
   * Built once and never played: it exists so the title screen is not a black
   * rectangle, and so the 3D renderer is visibly doing something before the
   * player has pressed anything. It is a real `Level` -- not a special backdrop
   * mode -- because a second code path for "just draw scenery" is exactly the
   * kind of thing that rots while nobody is looking at the menus.
   */
  private backdrop: Level | null = null

  private drawMenuBackdrop(): void {
    if (this.backdrop === null) {
      const data = cloneLevelData(COURSE_LIST[0] ?? emptyLevel('Backdrop'))
      this.backdrop = new Level(data)
      backdropCamera.setBounds(data.width, data.height)
      backdropCamera.x = 0
    }
    // A slow rightward drift, wrapped at the level edge, so the scene reads as
    // live rather than as a still. `tick` advances because several props animate
    // off it -- a frozen clock would leave the coins and saws mid-spin.
    this.backdrop.tick++
    const limit = Math.max(1, this.backdrop.width * TILE - VIEW_W)
    backdropCamera.x = (backdropCamera.x + 0.15) % limit
    renderer3d.renderLevel(this.backdrop, backdropCamera)
  }

  /**
   * A screen-space toast, drawn last so it survives whichever state we are in.
   *
   * It exists because of what the editor has to say on the way out: it stops
   * rendering the moment it closes, so its own toast can never be seen, and
   * "saved as Green Hills 2" is not something the player should have to work
   * out from a list that just grew.
   */
  private drawNotice(): void {
    const notice = this.notice
    if (notice === null || performance.now() > notice.until) return
    applyUiTransform(ctx)
    ctx.font = NOTICE_FONT
    ctx.textAlign = 'center'
    const w = Math.min(DESIGN_W - 80, ctx.measureText(notice.text).width + 48)
    ctx.fillStyle = 'rgba(0,0,0,0.78)'
    const y =
      this.state === 'play' || this.state === 'dead' || this.state === 'paused'
        ? 120
        : 24
    ctx.fillRect(DESIGN_W / 2 - w / 2, y, w, 44)
    this.text(notice.text, DESIGN_W / 2, y + 30, 24, '#85ded1')
  }

  /**
   * Wipe the screen before drawing UI. The UI transform is active, so this
   * fills the whole design space regardless of the current art scale.
   */
  private clearScreen(colour: string): void {
    ctx.fillStyle = colour
    ctx.fillRect(0, 0, DESIGN_W, DESIGN_H)
  }

  private drawPlay(): void {
    const lv = this.level
    if (lv === null) return
    // The world goes to the WebGL layer; the HUD and debug overlays are 2D and
    // share one integer transform on the interface layer above it.
    renderer3d.renderLevel(lv, camera)
    applyWorldTransform(ctx)
    if (
      this.state === 'play' ||
      this.state === 'dead' ||
      this.state === 'paused'
    ) {
      drawHudPopups(ctx, camera)
      drawHud(ctx, lv, {
        // The hurry flash is normal play; a paused clock holds still so the
        // dimmed frame behind the banner is not animating under it.
        blink: this.state !== 'paused',
        world: this.worldLabel(lv.data.name),
      })
    }
    debugDraw(lv, camera)
    if (this.state === 'paused') {
      // The simulation is stopped while paused, so the frozen frame behind the
      // banner is the whole point: it shows exactly what the player returns to.
      applyUiTransform(ctx)
      this.drawPauseBanner()
    }
    if (this.state === 'won') {
      // The win banner is UI, so it needs the UI transform and a letterbox-safe
      // dim over the whole framebuffer rather than just the world view.
      applyUiTransform(ctx)
      this.drawWinBanner()
    }
  }

  // ── menus (drawn in 1280x720 canvas space) ────────────────────────────

  /** Shared centred-text helper; keeps the menus consistent. */
  private text(
    str: string,
    x: number,
    y: number,
    size: number,
    colour: string,
    align: CanvasTextAlign = 'center',
  ): void {
    ctx.font = `bold ${size}px ui-monospace, "SF Mono", Menlo, monospace`
    ctx.textAlign = align
    ctx.fillStyle = colour
    ctx.fillText(str, x, y)
  }

  private drawTitle(): void {
    // The 3D scene is live behind this screen, so the menu dims it rather than
    // replacing it: an opaque fill would throw away the one thing the title
    // screen is advertising.
    ctx.fillStyle = 'rgba(11,16,48,0.68)'
    ctx.fillRect(0, 0, DESIGN_W, DESIGN_H)

    this.text("TAVI'S WORKSHOP", DESIGN_W / 2, 300, 66, '#85ded1')
    this.text('build a world, light the way', DESIGN_W / 2, 352, 26, '#ffffff')

    if (Math.floor(performance.now() / 400) % 2 === 0) {
      this.text(
        touchActive ? 'TAP TO PLAY' : 'PRESS SPACE',
        DESIGN_W / 2,
        470,
        30,
        '#85ded1',
      )
    }
    this.text(
      touchActive
        ? 'Swipe to choose a course        ↻ landscape is best'
        : 'E  edit a course        F  fullscreen',
      DESIGN_W / 2,
      DESIGN_H - 60,
      18,
      '#9a9ab8',
    )
  }

  private drawSelect(): void {
    // Dim the live 3D workshop behind the menu, matching the title screen.
    ctx.fillStyle = 'rgba(11,16,48,0.68)'
    ctx.fillRect(0, 0, DESIGN_W, DESIGN_H)

    const list = this.courses()
    const { workshopEnd } = chapterBounds(list)
    const inWorkshop =
      this.selected < workshopEnd || workshopEnd === list.length
    const selected = list[this.selected]

    this.text('SELECT COURSE', 48, 56, 34, '#85ded1', 'left')

    // Chapter tabs.
    const tabY = 88
    const drawTab = (label: string, x: number, active: boolean): void => {
      this.text(label, x, tabY, 18, active ? '#85ded1' : '#6a6a86', 'left')
      if (active) {
        ctx.fillStyle = '#d59159'
        const w = label.length * 11
        ctx.fillRect(x, tabY + 8, w, 3)
      }
    }
    drawTab('1 · WORKSHOP', 48, inWorkshop)
    drawTab('2 · BLUEPRINTS', 280, !inWorkshop && workshopEnd < list.length)

    // List panel.
    const listX = 40
    const listW = 720
    const top = 120
    const rowH = 56
    const footerY = DESIGN_H - 56
    const previewX = 790
    const visible = Math.max(1, Math.floor((footerY - 36 - top) / rowH))
    const { first } = scrollWindow(this.selected, list.length, visible)

    ctx.fillStyle = 'rgba(21,36,45,0.82)'
    ctx.fillRect(listX, top - 20, listW, visible * rowH + 28)
    ctx.strokeStyle = 'rgba(156,105,79,0.45)'
    ctx.lineWidth = 2
    ctx.strokeRect(listX + 1, top - 19, listW - 2, visible * rowH + 26)

    if (first > 0) {
      this.text(`▲ ${first} above`, listX + listW / 2, top - 6, 14, '#9c694f')
    }

    for (let row = 0; row < visible; row++) {
      const course = list[first + row]
      if (course === undefined) continue
      const y = top + row * rowH + 18
      const sel = first + row === this.selected
      const rowTop = y - 22

      if (sel) {
        ctx.fillStyle = 'rgba(41,68,89,0.9)'
        ctx.fillRect(listX + 8, rowTop, listW - 16, 48)
        ctx.fillStyle = '#85ded1'
        ctx.fillRect(listX + 8, rowTop, 4, 48)
      }

      const swatch = themeColor(course.data.theme)
      ctx.fillStyle = swatch
      ctx.fillRect(listX + 22, rowTop + 14, 18, 18)
      ctx.strokeStyle = sel ? '#85ded1' : '#9c694f'
      ctx.lineWidth = 1
      ctx.strokeRect(listX + 22, rowTop + 14, 18, 18)

      const label = this.worldLabel(course.name)
      const diff = courseDifficulty(course.name, course.builtIn)
      const diffColour =
        diff === 'EASY'
          ? '#a1ccb3'
          : diff === 'HARD'
            ? '#be5861'
            : diff === 'CUSTOM'
              ? '#d59159'
              : '#e8b971'

      this.text(
        `${label}  ${course.name}`,
        listX + 52,
        y - 2,
        22,
        sel ? '#85ded1' : '#c8c8dc',
        'left',
      )
      this.text(
        `${course.data.theme} · ${course.data.time}s`,
        listX + 52,
        y + 18,
        14,
        '#6a6a86',
        'left',
      )

      ctx.fillStyle = 'rgba(23,47,60,0.95)'
      ctx.fillRect(listX + listW - 86, rowTop + 12, 62, 22)
      ctx.strokeStyle = diffColour
      ctx.strokeRect(listX + listW - 86, rowTop + 12, 62, 22)
      this.text(diff, listX + listW - 55, rowTop + 28, 13, diffColour)
    }

    const below = Math.max(0, list.length - (first + visible))
    if (below > 0) {
      this.text(
        `▼ ${below} below`,
        listX + listW / 2,
        top + visible * rowH + 4,
        14,
        '#9c694f',
      )
    }

    // Preview strip.
    ctx.fillStyle = 'rgba(23,47,60,0.88)'
    ctx.fillRect(previewX, top - 20, 450, visible * rowH + 28)
    ctx.strokeStyle = 'rgba(133,222,209,0.35)'
    ctx.lineWidth = 2
    ctx.strokeRect(previewX + 1, top - 19, 448, visible * rowH + 26)

    if (selected !== undefined) {
      const sw = themeColor(selected.data.theme)
      ctx.fillStyle = sw
      ctx.fillRect(previewX + 24, top + 8, 400, 72)
      ctx.fillStyle = 'rgba(11,16,48,0.35)'
      ctx.fillRect(previewX + 24, top + 8, 400, 72)

      this.text(
        this.worldLabel(selected.name),
        previewX + 36,
        top + 40,
        18,
        '#fff3d8',
        'left',
      )
      this.text(selected.name, previewX + 36, top + 68, 28, '#85ded1', 'left')
      this.text(
        courseBlurb(selected.name),
        previewX + 36,
        top + 120,
        16,
        '#c8c8dc',
        'left',
      )
      this.text(
        `${selected.data.theme.toUpperCase()}    ${selected.data.time}s    ${selected.data.objects.length} parts`,
        previewX + 36,
        top + 158,
        15,
        '#9a9ab8',
        'left',
      )
      this.text(
        selected.builtIn ? 'SHIPPED FROM THE WORKSHOP' : 'YOUR BLUEPRINT',
        previewX + 36,
        top + 190,
        15,
        selected.builtIn ? '#557b94' : '#d59159',
        'left',
      )
      this.text(
        touchActive ? 'TAP TO PLAY' : 'SPACE TO PLAY',
        previewX + 36,
        top + 240,
        20,
        '#85ded1',
        'left',
      )
    } else {
      this.text('NO COURSES', previewX + 225, top + 120, 22, '#be5861')
    }

    this.text(
      `${list.length} COURSES`,
      DESIGN_W / 2,
      footerY - 10,
      15,
      '#4a4a60',
    )
    this.text(
      touchActive
        ? 'Swipe · hold to scroll    flick ←→ chapters    TAP play    E edit'
        : 'W/S select    A/D chapters    SPACE play    E edit    ESC back',
      DESIGN_W / 2,
      footerY + 14,
      16,
      '#60607a',
    )
  }

  private drawWinBanner(): void {
    const t = Math.min(1, this.winTimer / 0.4)
    ctx.fillStyle = `rgba(0,0,0,${0.55 * t})`
    ctx.fillRect(0, 0, DESIGN_W, DESIGN_H)
    this.text('COURSE CLEAR!', DESIGN_W / 2, DESIGN_H / 2 - 36, 56, '#85ded1')
    const lv = this.level
    const secondsLeft = Math.max(0, Math.ceil(lv?.stats.time ?? 0))
    const timeBonus = lv?.timeBonus ?? 0
    this.text(
      `SHARDS ${this.totalCoins}    SCORE ${lv?.stats.score ?? 0}`,
      DESIGN_W / 2,
      DESIGN_H / 2 + 18,
      22,
      '#ffffff',
    )
    this.text(
      `TIME LEFT ${secondsLeft}    BONUS ${timeBonus}`,
      DESIGN_W / 2,
      DESIGN_H / 2 + 52,
      18,
      '#fcd800',
    )
    if (this.winTimer > 1.2) {
      const list = this.courses()
      const at = list.findIndex((entry) => entry.name === lv?.data.name)
      const hasNext = at >= 0 && at + 1 < list.length
      const nextName = hasNext ? (list[at + 1]?.name ?? null) : null
      const nextLabel =
        nextName !== null ? `${this.worldLabel(nextName)} ${nextName}` : null
      this.text(
        touchActive
          ? hasNext
            ? `TAP for ${nextLabel ?? 'next'}`
            : 'TAP FOR COURSES'
          : hasNext
            ? `SPACE  ${nextLabel ?? 'next course'}        ESC  courses`
            : 'SPACE / ESC  courses',
        DESIGN_W / 2,
        DESIGN_H / 2 + 96,
        18,
        '#9a9ab8',
      )
    }
  }

  private drawGameOver(): void {
    this.clearScreen('#000000')
    this.text('GAME OVER', DESIGN_W / 2, DESIGN_H / 2 - 20, 52, '#be5861')
    this.text(
      touchActive
        ? 'RETRY or COURSES below'
        : 'SPACE  retry        ESC  courses',
      DESIGN_W / 2,
      DESIGN_H / 2 + 30,
      20,
      '#ffffff',
    )
  }

  /** The pause overlay: a dimmed, frozen frame with the way out on it. */
  private drawPauseBanner(): void {
    ctx.fillStyle = 'rgba(8,10,24,0.62)'
    ctx.fillRect(0, 0, DESIGN_W, DESIGN_H)
    this.text('PAUSED', DESIGN_W / 2, DESIGN_H / 2 - 16, 52, '#85ded1')
    this.text(
      touchActive
        ? 'Use the buttons below'
        : 'ESC  resume        Q  quit to courses',
      DESIGN_W / 2,
      DESIGN_H / 2 + 36,
      20,
      '#ffffff',
    )
  }
}

/**
 * Fit the fixed 720p buffer to the window under the current scaling mode.
 *
 * The backing store is *always* 1280x720; only the CSS display size changes.
 * In `integer` mode the CSS size is a whole multiple of 720p, so the
 * compositor's final blit is 1:1 on art pixels and they stay exact. In `fill`
 * mode it is whatever covers the window, which may be fractional.
 */
function syncCanvasSize(): void {
  // The hint and buttons are absolutely positioned overlays, so they never take
  // layout space from the canvas.
  const viewport = document.getElementById('viewport')!
  const fit = fitDisplay(view.mode, viewport.clientWidth, viewport.clientHeight)
  const frame = document.getElementById('frame')
  if (frame) {
    frame.style.width = `${fit.w}px`
    // The editor chrome is one fixed 1280x720 layer (editor/chrome.css) scaled
    // as a whole, so its layout and type are proportional on every display
    // instead of reflowing per device. Same design space as the interface
    // canvas, which is what keeps the two aligned.
    frame.style.setProperty('--ui-scale', String(fit.w / DESIGN_W))
  }
  view.displayWidth = fit.w
  view.displayHeight = fit.h

  // Backing store never changes; assert it rather than assume it.
  if (canvas.width !== DESIGN_W) canvas.width = DESIGN_W
  if (canvas.height !== DESIGN_H) canvas.height = DESIGN_H
  canvas.style.width = `${fit.w}px`
  canvas.style.height = `${fit.h}px`

  // The 3D layer is a separate canvas showing the same CSS box, so the two
  // stay pixel-aligned at every window size. Its backing store is 320x180 --
  // one art pixel per game pixel -- and the compositor scales it up, which is
  // why the art is filtered NEAREST in the scene rather than resampled here.
  worldCanvas.style.width = `${fit.w}px`
  worldCanvas.style.height = `${fit.h}px`

  ctx.imageSmoothingEnabled = false
  renderer3d.resize()
  syncScalingButton()
}

function cycleScalingMode(): void {
  const i = SCALING_MODES.indexOf(view.mode)
  view.mode = SCALING_MODES[(i + 1) % SCALING_MODES.length] ?? 'fill'
  saveScalingMode(view.mode)
  syncCanvasSize()
  console.log(`[tavi] scaling: ${view.mode}`)
}

view.mode = loadScalingMode()

window.addEventListener('resize', syncCanvasSize)
// Safe-area changes on rotation/resume can settle after the window resize.
// Observe the actual CSS box rather than guessing iOS viewport corrections.
new ResizeObserver(syncCanvasSize).observe(document.getElementById('viewport')!)

// ── fullscreen ─────────────────────────────────────────────────────────

const stage = document.getElementById('stage') as HTMLElement | null
const fsButton = document.getElementById('fs') as HTMLButtonElement | null
const scaleButton = document.getElementById('scale') as HTMLButtonElement | null
const soundButton = document.getElementById('sound') as HTMLButtonElement | null
function syncSoundButton(): void {
  if (soundButton === null) return
  const muted = isAudioMuted()
  soundButton.textContent = muted ? 'Sound: off' : 'Sound: on'
  soundButton.title = muted ? 'Enable sound' : 'Mute sound'
  soundButton.setAttribute('aria-pressed', String(muted))
}
soundButton?.addEventListener('click', () => {
  setAudioMuted(!isAudioMuted())
  resumeAudio()
  syncSoundButton()
  canvas.focus()
})
syncSoundButton()

function syncScalingButton(): void {
  if (scaleButton === null) return
  scaleButton.textContent =
    view.mode === 'integer' ? 'Scaling: 1:1' : 'Scaling: fill'
  scaleButton.title =
    view.mode === 'integer'
      ? 'Showing at whole multiples of 720p: pixel-exact, with black bars. Click to fill the window.'
      : 'Filling the window: the image may be scaled fractionally. Click for pixel-exact integer scaling.'
}

scaleButton?.addEventListener('click', () => cycleScalingMode())

function isFullscreen(): boolean {
  return document.fullscreenElement != null
}

const installedDisplay = window.matchMedia(
  '(display-mode: standalone), (display-mode: fullscreen)',
)

function isInstalled(): boolean {
  return (
    installedDisplay.matches ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true
  )
}

async function toggleFullscreen(): Promise<void> {
  try {
    if (isFullscreen()) {
      await document.exitFullscreen()
    } else if (stage !== null && document.fullscreenEnabled && !isInstalled()) {
      await stage.requestFullscreen()
    }
  } catch (err) {
    // Fullscreen can be refused (no user gesture, iframe policy). The game is
    // still perfectly playable windowed, so this must not be fatal.
    console.warn('[tavi] fullscreen refused:', err)
  }
}

function syncFullscreenUi(): void {
  if (fsButton !== null) {
    fsButton.hidden =
      !isFullscreen() && (!document.fullscreenEnabled || isInstalled())
    fsButton.textContent = isFullscreen() ? 'Windowed' : 'Fullscreen'
  }
  canvas.focus()
}

fsButton?.addEventListener('click', () => {
  void toggleFullscreen()
})
document.addEventListener('fullscreenchange', syncFullscreenUi)
installedDisplay.addEventListener('change', syncFullscreenUi)
syncFullscreenUi()

// First sizing pass. This lives here rather than next to `syncCanvasSize`
// because it also refreshes the toolbar, whose element bindings are `const`
// declared below — calling it earlier threw a temporal-dead-zone
// ReferenceError during module init and took the whole app with it.
syncCanvasSize()

/**
 * `F` toggles fullscreen without reaching for the mouse.
 *
 * Suppressed while the editor is open, because the editor binds `F` to the
 * rectangle-fill tool. A global shortcut that silently also fires a tool is the
 * kind of conflict nobody notices until a level comes out with a rectangle of
 * the wrong terrain in it.
 */
window.addEventListener('keydown', (e) => {
  if (e.repeat) return
  if (app.inEditor) return
  if (e.code === 'KeyF' && !e.ctrlKey && !e.metaKey && !e.altKey) {
    void toggleFullscreen()
  }
})

// Clicking the canvas focuses it, so the keyboard works without a click first.
canvas.addEventListener('pointerdown', () => {
  canvas.focus()
})
window.addEventListener('pointerdown', () => resumeAudio(), {
  passive: true,
  capture: true,
})
window.addEventListener('keydown', () => resumeAudio(), { passive: true })

const app = new App()
window.addEventListener('popstate', () => app.restorePreview())
document
  .getElementById('back-editor')!
  .addEventListener('click', () => app.backFromPreview())
document.getElementById('watch-bot')?.addEventListener('click', () => {
  app.watchBot()
})

/**
 * On-screen controls for touch devices.
 *
 * Created here, after the app, because it feeds the same `Input` the shell
 * reads; `syncTouchControls` above is the one hook `render` calls, so the
 * layers follow the screen without `App` needing a reference to them.
 */
const touchControls =
  stage === null ? null : new TouchControls({ input, root: stage })
if (touchControls !== null) {
  syncTouchControls = (state) => {
    touchControls.applyState(state)
    touchActive = touchControls.active
  }
  touchControls.attach()
}

const loop = new GameLoop({
  update: () => {
    input.beginFrame()
    app.update()
    input.endFrame()
  },
  render: () => app.render(),
})
loop.start()

// Dev-only debug handle. Lets you poke at live state from the console, and lets
// the browser-driven smoke tests assert on real values rather than pixels.
// See src/core/debug.ts for what it exposes and why it exists.
if (import.meta.env.DEV) {
  setDebugCourses(listCourses().map((c) => c.name))
  const debug = createDebug(
    {
      getState: () => app.state,
      getInEditor: () => app.inEditor,
      getLevel: () => app.level,
      startCourse: (i) => app.startCourse(i),
      retry: () => app.retry(),
      toggleEditor: () => app.toggleEditor(),
      log: app.log,
      recorder: app.recorder,
      rng: app.rng,
      sceneStats: () => app.sceneStats(),
      setDebugFlags: (f) => app.setDebugFlags(f),
      debugFlags: () => app.debugFlags,
      noteDebugKill: () => app.noteDebugKill(),
      get totalCoins() {
        return app.totalCoins
      },
    },
    player,
    camera,
    () => loop.fps,
    () => loop.timing,
  )
  const w = window as unknown as Record<string, unknown>
  w.__debug = debug
  debugDraw = (lvl, cam) => debug.draw(ctx, lvl, cam)
  // Raw handles too, for when the API does not cover what you are chasing.
  w.__tavi = { app, player, camera, input, loop, renderer3d }
  console.log(
    '%c[tavi]%c debug ready \u2014 try __debug.snapshot(), __debug.flags_({grid:true,hitbox:true})',
    'color:#85ded1;font-weight:bold',
    'color:#888',
  )
}

// Surface unexpected errors instead of failing silently.
window.addEventListener('error', (e) => {
  console.error('[tavi]', e.error ?? e.message)
})

// Accept renderer dependencies without replacing the app, loop, input, or editor.
if (import.meta.hot) {
  import.meta.hot.accept('./engine3d/scene', (module) => {
    if (module === undefined) return
    const debug = (window as unknown as { __debug: { snapshot(): unknown } })
      .__debug
    const before = debug.snapshot()
    renderer3d.dispose()
    liveRenderer.replace(new module.Renderer3D(worldCanvas))
    syncCanvasSize()
    console.log('[tavi] renderer hot-swapped; game and editor state preserved')
    console.log(
      '[tavi] HMR snapshots',
      JSON.stringify({ before, after: debug.snapshot() }),
    )
  })
}
